import { Box } from '@mui/material';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { RenderingCancelledException, TextLayer } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';

interface Props {
    pdf: PDFDocumentProxy;
    pageNumber: number;
    scale: number;
    /** Placeholder size (base page size × scale) used until the page is rendered. */
    estimatedWidth: number;
    estimatedHeight: number;
}

interface PageSize {
    width: number;
    height: number;
    /** The scale the size was measured at, so stale sizes are ignored after zooming. */
    scale: number;
}

/**
 * Renders a single PDF page: a canvas for the page image plus an overlaid
 * pdf.js text layer for text selection. Rendering is deferred until the page
 * scrolls near the viewport.
 */
export default function PdfPage({
    pdf,
    pageNumber,
    scale,
    estimatedWidth,
    estimatedHeight,
}: Props) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textLayerRef = useRef<HTMLDivElement>(null);
    const [pageSize, setPageSize] = useState<PageSize | null>(null);
    const [shouldRender, setShouldRender] = useState(false);

    // Defer rendering until the page is within one viewport height of view.
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setShouldRender(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '100% 0px' },
        );
        observer.observe(wrapper);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!shouldRender) return;

        let cancelled = false;
        let renderTask: RenderTask | undefined;
        let textLayer: TextLayer | undefined;

        (async () => {
            const page = await pdf.getPage(pageNumber);
            const canvas = canvasRef.current;
            const textLayerDiv = textLayerRef.current;
            if (cancelled || !canvas || !textLayerDiv) return;

            const viewport = page.getViewport({ scale });
            setPageSize({ width: viewport.width, height: viewport.height, scale });

            // Render at device pixel ratio (capped) for crisp text on hi-DPI screens.
            const outputScale = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.floor(viewport.width * outputScale);
            canvas.height = Math.floor(viewport.height * outputScale);

            renderTask = page.render({
                canvas,
                viewport,
                transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
            });
            await renderTask.promise;
            if (cancelled) return;

            textLayerDiv.replaceChildren();
            textLayerDiv.style.setProperty('--total-scale-factor', String(viewport.scale));
            textLayer = new TextLayer({
                textContentSource: page.streamTextContent(),
                container: textLayerDiv,
                viewport,
            });
            await textLayer.render();
        })().catch((error: unknown) => {
            if (error instanceof RenderingCancelledException) return;
            console.error(`Failed to render PDF page ${pageNumber}:`, error);
        });

        return () => {
            cancelled = true;
            renderTask?.cancel();
            textLayer?.cancel();
        };
    }, [pdf, pageNumber, scale, shouldRender]);

    // Only trust the measured size while it matches the current scale, so the
    // layout updates synchronously when zooming.
    const measuredSize = pageSize?.scale === scale ? pageSize : null;

    return (
        <Box
            ref={wrapperRef}
            data-pdf-page={pageNumber}
            sx={{
                position: 'relative',
                width: measuredSize?.width ?? estimatedWidth,
                height: measuredSize?.height ?? estimatedHeight,
                flexShrink: 0,
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
        >
            <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
            <div ref={textLayerRef} className="textLayer" />
        </Box>
    );
}
